<template>
  <div class="website-page-layout">
    <Header
      class="website-page-layout__header"
      :class="{ 'website-page-layout__header_absolute': absoluteHeader }"
    />
    <Transition name="slide-up">
      <div class="website-page-layout__cover" v-if="!isHomePage">
        <Transition name="website-page-layout__image">
          <VideoOrImage
            class="website-page-layout__cover-background"
            :src="coverUri"
            :key="coverUri"
          />
        </Transition>
        <div class="container" data-aos="fade-right">
          <Transition
            name="website-page-layout__text"
            mode="out-in"
            :duration="300"
          >
            <p class="website-page-layout__title" :key="displayTitle!">
              {{ displayTitle }}
            </p>
          </Transition>
        </div>
      </div>
    </Transition>
    <div class="website-page-layout__content">
      <slot />
    </div>
    <Footer class="website-page-layout__footer" />
    <ClientOnly>
      <Transition name="fade">
        <div class="website-page-layout__scroll-to-top-button" v-if="y > 0">
          <ScrollToTopButton />
        </div>
      </Transition>
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
  import defaultCoverUri from "@/assets/images/page-cover.webp";

  const props = defineProps<{
    absoluteHeader?: boolean;
    coverUri?: string;
    displayTitle?: string;
  }>();

  const { getCurrentPageSettings } = useWebsiteContent();

  const { data: pageSettings, suspense: suspensePageSettings } = getCurrentPageSettings();
  
  const { y } = useScroll(window);
  const route = useRoute();
  
  const isHomePage = computed(() => route.path === "/");
  
  const coverUri = computed<string>(
    () =>
    props.coverUri ??
    pageSettings.value?.cover.backgroundUri ??
    defaultCoverUri
  );
  const displayTitle = computed<string | null>(
    () => props.displayTitle ?? pageSettings.value?.cover.title ?? null
  );
  
  await suspensePageSettings();
</script>

<style lang="scss">
  .website-page-layout {
    position: relative;

    &__header {
      position: fixed;
      width: 100%;

      z-index: 100;

      &_absolute {
        position: absolute;
      }
    }

    &__cover {
      display: flex;
      align-items: center;

      position: relative;

      height: 384px;

      background-color: #182656;
      background-size: cover;
      background-position: center;

      @media (max-width: $lg) {
        height: 128px;
      }
    }

    &__cover-background {
      position: absolute !important;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;

      opacity: 0.3;
    }

    &__title {
      color: white;
      font-family: var(--font-family-main);
      font-size: 72px;
      font-weight: 700;
      text-transform: uppercase;

      @media (max-width: $lg) {
        display: none;
      }
    }

    &__scroll-to-top-button {
      position: fixed !important;
      right: 32px;
      bottom: 32px;
    }
  }

  .website-page-layout__image-enter-active,
  .website-page-layout__image-leave-active {
    transition: opacity 600ms ease;
  }

  .website-page-layout__image-enter-from,
  .website-page-layout__image-leave-to {
    opacity: 0;
  }

  .website-page-layout__text-enter-active,
  .website-page-layout__text-leave-active {
    transition: all 200ms ease-out;
  }

  .website-page-layout__text-enter-from,
  .website-page-layout__text-leave-to {
    transform: translateX(-20px);
    opacity: 0;
  }

  .slide-up-enter-active,
  .slide-up-leave-active {
    transition: all 200ms ease-out;
  }

  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: translateY(-100%);
  }
</style>
